/**
 * State 3 — Free user, at least one free course COMPLETED.
 * Goal: convert to Premium. Upgrade prompt is the most dominant element.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardUser } from './types';
import { HeroBanner } from './shared/HeroBanner';
import { CourseItem } from './shared/CourseItem';
import { LockedPlan } from './shared/LockedPlan';
import { TodayGoal, Goal } from './shared/TodayGoal';
import { ROUTES } from '../../utils/constants';

interface Props {
  user: DashboardUser;
}

const UPGRADE_FEATURES = [
  'Full personalised learning plan',
  'New courses added every month',
  'Templates & AI prompts library',
  'Private community access',
];

export const DashState3CourseCompleted: React.FC<Props> = ({ user }) => {
  const [completedCourse] = user.completedCourses;
  const lockedCourses =
    user.assessmentResults?.matchedCourses.filter((c) => !c.isFree) ?? [];

  const goals: Goal[] = [
    { label: 'Complete 1 lesson', done: user.completedLessonToday },
    // Upgrade to Premium — always unchecked in State 3 by definition (free user)
    { label: 'Upgrade to unlock your plan', done: false },
    // TODO: wire to community posts when data is available (no existing query for all-time user posts)
    { label: 'Introduce yourself in the community', done: false },
  ];

  return (
    <div style={{ background: '#f7f8ff', minHeight: '100%' }}>
      <main className="container-custom py-10">
        {/* Hero */}
        <HeroBanner
          headline={`You're off to a great start, ${user.name}. Ready to go deeper?`}
          sub={`Your personalised plan has ${lockedCourses.length > 0 ? lockedCourses.length : 3} more courses waiting — built specifically around your goals.`}
          chip="🎉 First course done!"
          chipVariant="amber"
          animDelay={0}
        />

        {/* Mobile: Today's Goals immediately below hero */}
        <div className="block lg:hidden mb-6">
          <TodayGoal goals={goals} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── LEFT COLUMN (2/3) ────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
            {/* ── UPGRADE BANNER ───────────────────────────────────────── */}
            <div
              className="db-fade-up rounded-2xl p-6 md:p-8"
              style={{
                background: 'linear-gradient(135deg, #1e1e2e, #2d2d42)',
                border: '1.5px solid rgba(255,255,255,0.06)',
                boxShadow: '0 4px 24px rgba(30,30,46,0.20)',
                animationDelay: '100ms',
              }}
            >
              {/* Premium badge */}
              <div className="mb-4">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-montserrat font-bold text-sm text-white"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    boxShadow: '0 4px 14px rgba(245,158,11,0.30)',
                  }}
                >
                  ⚡ Premium
                </span>
              </div>

              <h2 className="font-montserrat font-extrabold text-2xl text-white mb-3">
                Unlock your full learning plan
              </h2>
              <p className="font-open-sans text-sm text-neutral-300 leading-relaxed mb-5 max-w-lg">
                Your assessment matched you with 4 courses. You've completed 1. Upgrade
                to access the rest — and every new course we release.
              </p>

              {/* Feature bullets */}
              <ul className="space-y-2 mb-6">
                {UPGRADE_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <span style={{ color: '#00c2a8', fontSize: '15px' }}>✓</span>
                    <span className="font-open-sans text-sm text-neutral-200">{f}</span>
                  </li>
                ))}
              </ul>

              {/* Testimonial */}
              <div
                className="rounded-xl p-4 mb-6"
                style={{ background: 'rgba(255,255,255,0.07)' }}
              >
                <p className="font-open-sans text-sm text-neutral-300 italic leading-relaxed mb-2">
                  "After my first course I upgraded immediately. The second course alone
                  saved me 4 hours the following week."
                </p>
                <p className="font-open-sans text-xs text-neutral-400">
                  Mike R. · Restaurant Owner · USA
                </p>
              </div>

              {/* CTA aligned right */}
              <div className="flex justify-end">
                <Link
                  to={ROUTES.pricing}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-montserrat font-bold text-base text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    boxShadow: '0 4px 18px rgba(245,158,11,0.35)',
                  }}
                >
                  Upgrade Now →
                </Link>
              </div>
            </div>

            {/* ── COMPLETED COURSE ─────────────────────────────────────── */}
            {completedCourse && (
              <div
                className="db-fade-up"
                style={{ animationDelay: '200ms' }}
              >
                <h3 className="font-montserrat font-bold text-sm text-neutral-800 mb-3">
                  ✅ Completed
                </h3>
                <CourseItem
                  course={completedCourse}
                  variant="completed"
                />
              </div>
            )}

            {/* ── LOCKED PLAN ──────────────────────────────────────────── */}
            {lockedCourses.length > 0 && (
              <div
                className="db-fade-up"
                style={{ animationDelay: '300ms' }}
              >
                <h3 className="font-montserrat font-bold text-sm text-neutral-800 mb-3">
                  Your learning plan
                </h3>
                <LockedPlan courses={lockedCourses} showUpgradeButton={true} />
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR (1/3) ──────────────────────────────────── */}
          <div className="space-y-5">
            {/* Today's Goals — first card in sidebar, hidden on mobile (shown above hero on mobile) */}
            <div className="db-fade-up hidden lg:block" style={{ animationDelay: '100ms' }}>
              <TodayGoal goals={goals} />
            </div>

            {/* Progress ring — SVG */}
            <div
              className="db-fade-up rounded-2xl p-5 bg-white text-center"
              style={{ border: '1.5px solid #e8e8ee', animationDelay: '100ms' }}
            >
              <p className="font-montserrat font-bold text-sm text-neutral-800 mb-4">
                Your Progress
              </p>
              <XPRing xp={user.xp} pct={user.aiReadinessPct} />
              <div className="mt-4 space-y-1">
                <p className="font-montserrat font-bold text-lg text-neutral-800">
                  {user.xp} XP
                </p>
                <p className="font-open-sans text-xs text-neutral-500">
                  {user.completedCourses.length} course
                  {user.completedCourses.length !== 1 ? 's' : ''} complete
                </p>
                <p
                  className="font-open-sans text-xs font-semibold"
                  style={{ color: '#5b6cff' }}
                >
                  Next milestone: 100 XP
                </p>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

/* ── Inline SVG progress ring ──────────────────────────────────────── */
const XPRing: React.FC<{ xp: number; pct: number }> = ({ pct }) => {
  const R = 44;
  const circ = 2 * Math.PI * R;
  const offset = circ - (pct / 100) * circ;

  return (
    <svg width="112" height="112" viewBox="0 0 112 112" className="mx-auto">
      <defs>
        <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5b6cff" />
          <stop offset="100%" stopColor="#00c2a8" />
        </linearGradient>
      </defs>
      {/* Track */}
      <circle
        cx="56"
        cy="56"
        r={R}
        fill="none"
        stroke="#e8e8ee"
        strokeWidth="10"
      />
      {/* Progress */}
      <circle
        cx="56"
        cy="56"
        r={R}
        fill="none"
        stroke="url(#ringGrad)"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 56 56)"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      {/* Centre text */}
      <text
        x="56"
        y="52"
        textAnchor="middle"
        fontFamily="Montserrat, sans-serif"
        fontWeight="800"
        fontSize="18"
        fill="#5b6cff"
      >
        {Math.round(pct)}%
      </text>
      <text
        x="56"
        y="67"
        textAnchor="middle"
        fontFamily="Open Sans, sans-serif"
        fontSize="10"
        fill="#9ca3af"
      >
        readiness
      </text>
    </svg>
  );
};
